/* eslint-disable sort-keys */
import React from 'react'

import { Icon } from '@clientbase/clientbase-library'
import { Box, hexToRgb, Tooltip } from '@mui/material'
import { CustomerParams } from 'models/Customers'
import Link from 'next/link'

import { ButtonRounded } from 'components/button'
import Can from 'components/Can'
import { PhoneDisplay } from 'components/PhoneDisplay'
import { TableNameWrapper, TableNickname } from 'components/Table/Table.styles'
import CustomerActionsPopover from 'components/v2/CustomerActionsPopover'

import {
  formatInternationalPhone,
  isInternationalPhone
} from 'utils/FormatPhone'

import { theme } from 'styles/theme'

import * as S from '../styles'

interface ReturnCreateDataTableParams {
  name: React.ReactNode | string | null | undefined
  email: React.ReactNode | string | null | undefined
  phone: React.ReactNode | string | null | undefined
  status: React.ReactNode | string | null | undefined
  action: React.ReactNode | null | undefined
  uuid: string
  group?: React.ReactNode | string | null | undefined
}

interface GroupInfo {
  uuid?: string
  name?: string
  image_url?: string
  group_color?: string
}

const getStatus = (status: string) => {
  switch (status) {
    case 'archived_customer':
      return {
        label: 'Arquivado',
        color: theme.palette.neutral[700],
        disabled: true
      }
    case 'inactive_customer':
      return {
        label: 'Inativo',
        color: theme.palette.alert[300]
      }
    case 'lead':
      return {
        label: 'Lead',
        color: theme.palette.secondary[300]
      }
    case 'active_customer':
      return {
        label: 'Ativo',
        color: theme.palette.primary.main
      }
    case 'churned':
      return {
        label: 'Churned',
        color: theme.palette.error[300]
      }
    case 'lost':
      return {
        label: 'Lost',
        color: theme.palette.neutral[300]
      }
    default:
      return {
        label: 'Status desconhecido',
        color: theme.palette.neutral[300]
      }
  }
}

export function createDataTable(
  customer: CustomerParams,
  actions: any,
  groupInfo?: GroupInfo
) {
  // Build group column JSX if groupInfo provided
  const groupColor = groupInfo?.group_color || '#d8d8d8'
  const rgbColor = hexToRgb(groupColor)
  const bgColorWithOpacity = rgbColor
    ? rgbColor.replace('rgb', 'rgba').replace(')', ', 0.6)')
    : 'rgba(216, 216, 216, 0.6)'

  const isNoGroup = groupInfo?.uuid === 'none'

  const groupColumn = groupInfo ? (
    isNoGroup ? (
      <Box
        sx={{
          color: '#777777',
          fontStyle: 'italic'
        }}
      >
        -
      </Box>
    ) : (
      <Tooltip
        title={groupInfo.name || ''}
        arrow
        placement="top"
        enterTouchDelay={0}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            minWidth: 32,
            minHeight: 32,
            flexShrink: 0,
            borderRadius: '50%',
            backgroundColor: bgColorWithOpacity,
            backgroundImage: groupInfo.image_url
              ? `url(${groupInfo.image_url})`
              : 'none',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer'
          }}
        >
          {!groupInfo.image_url && (
            <Icon icon="faUsers" sx={{ fontSize: 14, color: groupColor }} />
          )}
        </Box>
      </Tooltip>
    )
  ) : undefined

  const baseData: ReturnCreateDataTableParams = {
    uuid: customer?.uuid || '',
    name: (
      <Box sx={{ width: '100%', height: '100%' }}>
        <Link
          href={`/clientes/${customer?.uuid}`}
          onClick={(e) => {
            e.preventDefault()
          }}
          style={{
            all: 'unset',
            width: '100%',
            padding: '18px 0'
          }}
        >
          <TableNameWrapper>
            <p>{customer?.name}</p>
            <TableNickname>{customer?.nickname}</TableNickname>
          </TableNameWrapper>
        </Link>
      </Box>
    ),
    email: (
      <Box sx={{ width: '100%', height: '100%', display: 'flex' }}>
        <Link
          href={`/clientes/${customer?.uuid}`}
          onClick={(e) => {
            e.preventDefault()
          }}
          style={{
            all: 'unset',
            width: '100%',
            padding: '18px 0'
          }}
        >
          {customer.email}
        </Link>
      </Box>
    ),
    phone: (
      <Box sx={{ width: '100%', height: '100%', display: 'flex' }}>
        <Link
          href={`/clientes/${customer?.uuid}`}
          onClick={(e) => {
            e.preventDefault()
          }}
          style={{ all: 'unset', width: '100%', padding: '18px 0' }}
        >
          <PhoneDisplay value={customer?.phone ?? ''} size="small" />
        </Link>
      </Box>
    ),
    // Group column must be here (between phone and status) to match header order
    ...(groupColumn ? { group: groupColumn } : {}),
    status: (
      <S.Status status={customer?.category || ''}>
        {getStatus(customer?.category || '').label}
      </S.Status>
    ),
    action: (
      <Box
        display="flex"
        justifyContent="flex-end"
        onClick={(e) => e.stopPropagation()}
      >
        <Tooltip
          enterTouchDelay={0}
          arrow={true}
          placement="top"
          title="Gerenciar cliente"
        >
          <Box>
            <ButtonRounded
              onClick={() => {
                actions.handleOpenDetail(customer.uuid ? customer.uuid : '')
              }}
              variant="transparent"
            >
              <Icon
                icon="settings"
                sx={{ color: theme.palette.neutral[200] }}
              />
            </ButtonRounded>
          </Box>
        </Tooltip>

        <Can permission="billings:manage">
          <Tooltip
            enterTouchDelay={0}
            arrow={true}
            placement="top"
            title="Nova cobrança"
          >
            <Box>
              <ButtonRounded
                onClick={() =>
                  actions.handleOpenCharge({
                    label: (customer.name && customer.name) || '',
                    value: (customer.uuid && customer.uuid) || ''
                  })
                }
                variant="transparent"
                w="0px"
              >
                <Icon icon="paid" sx={{ color: theme.palette.neutral[200] }} />
              </ButtonRounded>
            </Box>
          </Tooltip>
        </Can>

        <CustomerActionsPopover
          customerUuid={customer.uuid || ''}
          customerName={customer.name || ''}
          customerDocument={customer.document || undefined}
          customerCategory={customer.category || undefined}
          status={customer.status || undefined}
          onDelete={() => actions.onDelete?.(customer.uuid)}
          onArchive={() => actions.onArchive?.(customer.uuid)}
          onUnarchive={() => actions.onUnarchive?.(customer.uuid)}
          onActionComplete={actions.onActionComplete}
          triggerButtonStyle={{
            backgroundColor: 'transparent'
          }}
          isTable
        />
      </Box>
    )
  }

  return baseData as unknown as ReturnCreateDataTableParams
}

/**
 * Máscara BR por tamanho, sem perguntar o país. O `PhoneDisplay` chama direto
 * com os dígitos nacionais depois de já ter decidido que o número é do Brasil;
 * passar de novo pela checagem faria "5511231231231" (DDI 55, sem o 9) virar
 * +1 no cabeçalho.
 */
export const maskBrazilianPhone = (phone: string) => {
  if (!phone) {
    return ''
  }

  const phoneLength = phone.length

  const phoneEnum = {
    8: {
      mask: () => {
        const parte1 = phone.slice(0, 4)
        const parte2 = phone.slice(4, 8)

        return `${parte1}-${parte2}`
      }
    },
    9: {
      mask: () => {
        const parte1 = phone.slice(0, 5)
        const parte2 = phone.slice(5, 9)
        return `${parte1}-${parte2}`
      }
    },
    10: {
      mask: () => {
        const parte1 = phone.slice(0, 2)
        const parte2 = phone.slice(2, 6)
        const parte3 = phone.slice(6, 15)

        return `(${parte1}) ${parte2}-${parte3}`
      }
    },
    11: {
      mask: () => {
        const parte1 = phone.slice(0, 2)
        const parte2 = phone.slice(2, 7)
        const parte3 = phone.slice(7, 15)

        return `(${parte1}) ${parte2}-${parte3}`
      }
    },
    12: {
      mask: () => {
        const parte1 = phone.slice(0, 2)
        const parte2 = phone.slice(2, 4)
        const parte3 = phone.slice(4, 8)
        const parte4 = phone.slice(8, 13)

        return `+${parte1} (${parte2}) ${parte3}-${parte4}`
      }
    },
    13: {
      mask: () => {
        const parte1 = phone.slice(0, 2)
        const parte2 = phone.slice(2, 4)
        const parte3 = phone.slice(4, 9)
        const parte4 = phone.slice(9, 14)

        return `+${parte1} (${parte2}) ${parte3}-${parte4}`
      }
    }
  }

  if (phoneLength >= 9 && phoneLength <= 13) {
    return phoneEnum[phoneLength as keyof typeof phoneEnum].mask()
  } else {
    return phone
  }
}

export const formatPhone = (phone: string) => {
  if (!phone) {
    return ''
  }

  if (isInternationalPhone(phone)) return formatInternationalPhone(phone)

  return maskBrazilianPhone(phone)
}
